/**
 * Impostazioni → Aggiornamenti.
 *
 * Mostra tre numeri e, quando serve, un pulsante. La parte delicata non è
 * quella che si vede ma quella che succede dopo il clic: **l'applicazione si
 * ferma**, quindi non arriverà nessuna risposta a dire com'è andata. La
 * schermata lo dice prima nel dialogo, e dopo resta su un messaggio d'attesa
 * invece di fingere di sapere.
 */
import { useCallback, useEffect, useState } from 'react';

import { aggiornamenti } from '../../api/scadenziario.ts';
import type { StatoAggiornamenti } from '../../api/tipi-dati.ts';
import { ErroreApi } from '../../api/client.ts';
import { useAvvisoFermo } from '../../componenti/AvvisoFermo.tsx';
import { Dialogo } from '../../componenti/Dialogo.tsx';
import { istante } from '../../componenti/formato.ts';
import { decidiCosaMostrare } from './aggiornamenti.ts';
import { StoricoAggiornamenti } from './StoricoAggiornamenti.tsx';

/*
 * ⚠️ Qui c'era `formattaIstante`, che toglieva a mano la `Z` finale prima di
 * chiamare `istante`. Non era una stranezza di questa pagina: era `istante`
 * che non sapeva leggere un istante col fuso dichiarato, e la toppa stava in
 * questo file invece che nella funzione. Chiunque altro chiamasse `istante` su
 * un timestamp dello stesso genere si ritrovava la stringa grezza a schermo —
 * ed è successo, alla colonna «Quando» dello storico. Sistemato alla radice in
 * `componenti/formato.ts`, dove il perché è scritto per esteso.
 */

export function Aggiornamenti() {
  const [stato, setStato] = useState<StatoAggiornamenti | null>(null);
  // La fase della striscia distingue «sta aspettando il preavviso» da «sta
  // costruendo»: due momenti che sul disco sono lo stesso `aggiornamentoInCorso`.
  const avviso = useAvvisoFermo();
  const [errore, setErrore] = useState<string | null>(null);
  const [chiedeConferma, setChiedeConferma] = useState(false);
  const [inviata, setInviata] = useState(false);
  const [inCorso, setInCorso] = useState(false);

  /**
   * Quanto preavviso dare. Cinque minuti come valore di partenza, non «subito»:
   * il caso normale è che qualcuno stia lavorando, e il valore predefinito
   * deve essere quello che protegge i colleghi. «Subito» resta a un clic di
   * distanza per la domenica sera.
   */
  const [preavviso, setPreavviso] = useState<0 | 300 | 900>(300);

  /**
   * `diFondo` distingue il giro periodico dal primo caricamento, e la
   * differenza sta tutta in che cosa succede se torna un 401.
   *
   * Chi arriva qui con la sessione morta va portato all'accesso: è appena
   * arrivato, non ha niente da perdere, e lasciarlo davanti a «Caricamento…»
   * per sempre sarebbe peggio. Chi invece è **rimasto** su questa schermata a
   * guardare un aggiornamento che ha appena lanciato non va portato da nessuna
   * parte: la sessione può compiere le dodici ore proprio mentre guarda, e
   * ritrovarsi al login senza aver toccato niente è la stessa regressione che
   * la striscia del fermo ha già chiuso per conto suo.
   */
  const carica = useCallback(async (diFondo = false) => {
    try {
      setStato(await (diFondo ? aggiornamenti.statoDiFondo() : aggiornamenti.stato()));
      setErrore(null);
    } catch (e) {
      // Anche di fondo l'errore si mostra: quello che cambia è che non porta
      // via nessuno. L'ultimo stato letto resta a schermo, con la riga di
      // errore sopra, e il giro dopo riprova.
      setErrore(e instanceof ErroreApi ? e.message : 'Impossibile leggere lo stato');
    }
  }, []);

  useEffect(() => {
    void carica();

    // La sentinella sul server guarda ogni minuto: ricontrollare più spesso
    // di così non mostrerebbe niente di nuovo.
    //
    // ⚠️ Qui c'erano cinque minuti, com'era la sentinella prima del 25/8/2026.
    // Quando la sentinella è scesa a un minuto questa riga è rimasta indietro:
    // la schermata poteva tardare cinque minuti a mostrare una versione che il
    // server conosceva da settanta secondi. I due orologi non sono in fase,
    // quindi il caso peggiore resta la somma dei due giri, non il maggiore.
    const timer = window.setInterval(() => void carica(true), 60 * 1000);
    return () => window.clearInterval(timer);
  }, [carica]);

  async function installa() {
    if (stato?.versioneDisponibile == null || inCorso) return;

    // Il pulsante si spegne mentre la richiesta è in volo. Non è cortesia:
    // qui la risposta può tardare, e un secondo clic manderebbe una seconda
    // POST. Il server la respingerebbe (il biglietto si scrive in esclusiva),
    // ma chi ha premuto vedrebbe un errore al posto della conferma per una
    // cosa che aveva fatto giusta.
    setInCorso(true);
    try {
      await aggiornamenti.installa(stato.versioneDisponibile, preavviso);
      setChiedeConferma(false);
      setInviata(true);
    } catch (e) {
      setChiedeConferma(false);
      setErrore(e instanceof ErroreApi ? e.message : 'Richiesta non riuscita');
    } finally {
      setInCorso(false);
    }
  }

  if (errore !== null && stato === null) {
    return <div className="avviso-errore" role="alert">{errore}</div>;
  }

  if (stato === null) {
    return <div className="pannello pannello-imbottito"><p className="stato-caricamento">Caricamento…</p></div>;
  }

  const situazione = decidiCosaMostrare(stato, avviso.fase);

  return (
    <>
      <div className="pannello pannello-imbottito">
        <h2 className="sezione-titolo">{situazione.titolo}</h2>
        <p className="aiuto" style={{ maxWidth: '70ch', marginBottom: 16 }}>{situazione.spiegazione}</p>

        <dl className="scheda-dati">
          <dt>Versione installata</dt>
          <dd>{stato.versioneInstallata}</dd>

          {stato.sistemaAttivo && (
            <>
              <dt>Versione disponibile</dt>
              <dd>{stato.versioneDisponibile ?? '—'}</dd>

              <dt>Ultimo controllo</dt>
              <dd>{stato.controllataIl === null ? 'mai' : istante(stato.controllataIl)}</dd>
            </>
          )}
        </dl>

        {inviata && (
          <div className="avviso-attenzione" role="status">
            Richiesta inviata. Il server costruisce la versione nuova (qualche minuto), poi
            avvisa tutti i collegati e si ferma. Questa pagina smetterà di rispondere per
            pochi secondi: è previsto. L'esito arriva su Telegram.
          </div>
        )}

        {errore !== null && !inviata && <div className="avviso-errore" role="alert">{errore}</div>}

        {situazione.pulsante && !inviata && (
          <button
            type="button"
            className="bottone bottone-principale"
            // ⚠️ Il preavviso torna al valore protettivo **a ogni apertura**.
            // Vive qui fuori, non dentro il dialogo, quindi senza questa riga
            // sopravvivrebbe alla chiusura: chi ha scelto «Subito» una domenica
            // sera, ha annullato, e riapre mercoledì alle 11 si troverebbe
            // «Subito» già selezionato. Una scelta pericolosa ereditata, nel
            // punto esatto in cui si ferma la produzione.
            onClick={() => { setPreavviso(300); setChiedeConferma(true); }}
          >
            Installa ora
          </button>
        )}
      </div>

      {/*
        * 🔴 **Qui c'erano `messaggio` e `azione`, ed erano testo libero scritto
        * dall'aggiornatore.** `messaggio` porta l'uscita grezza dei comandi (in
        * `catastrofe` è un `tail -5` del log di ripristino) e `azione` si porta
        * dietro `$avvertimento`, cioè i comandi di riparazione da dare a mano,
        * `sudo` e percorsi del volume compresi. Su questa pagina erano una mappa
        * del server per chi è amministratore **dell'applicazione**, che non è
        * chi ha accesso alla macchina.
        *
        * Tolti l'1 settembre 2026, su decisione di Enrico, quando lo storico è
        * diventato la coda di questa pagina e lo stesso tentativo ha cominciato
        * a comparire due volte — sopra col testo intero, sotto senza. La
        * riserva della spec del 10 agosto («i log raccontano molto anche a chi
        * non dovrebbe leggerli») o vale per tutti e due o non vale.
        *
        * ⚠️ **L'informazione non è andata persa**: il testo intero continua ad
        * arrivare su Telegram e a restare nel journal, dove lo legge chi ha già
        * accesso al server. Qui resta il `passo`, che dice DOVE si è rotto.
        * Se un giorno servisse a schermo, la strada è dare all'aggiornatore un
        * **codice** al posto del testo e far scegliere la frase
        * all'applicazione: il testo libero scritto da uno script non è
        * controllabile a priori da nessuno.
        */}
      {situazione.raccontaUltimoTentativo && stato.ultimoTentativo !== null && (
        <div className="pannello pannello-imbottito">
          <h2 className="sezione-titolo">Ultimo tentativo</h2>
          <dl className="scheda-dati">
            <dt>Versione</dt>
            <dd>{stato.ultimoTentativo.versione}</dd>
            <dt>Quando</dt>
            <dd>{istante(stato.ultimoTentativo.quando)}</dd>
            <dt>Esito</dt>
            <dd>{stato.ultimoTentativo.esito === 'fallito' ? 'Fallito' : 'Riuscito'}</dd>
            {stato.ultimoTentativo.passo !== null && (
              <>
                <dt>Passo</dt>
                <dd>{stato.ultimoTentativo.passo}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      {/* La coda della pagina, e solo dove un aggiornatore esiste: senza, la
          cima dice già «Aggiornamenti automatici non attivi» e una tabella
          vuota sotto ripeterebbe la stessa frase. */}
      {stato.sistemaAttivo && (
        <StoricoAggiornamenti ultimoTentativoQuando={stato.ultimoTentativo?.quando ?? null} />
      )}

      {chiedeConferma && (
        <Dialogo
          titolo={`Installare la versione ${stato.versioneDisponibile ?? ''}?`}
          aperto
          onChiudi={() => setChiedeConferma(false)}
        >
          <p>
            L'aggiornamento richiede qualche minuto, quasi tutti per costruire la versione
            nuova. <strong>L'applicazione resta irraggiungibile solo pochi secondi</strong>,
            alla fine.
          </p>
          <p>
            Chi sta lavorando vedrà un conto alla rovescia e potrà salvare quello che sta
            scrivendo. Scegli quanto tempo dargli:
          </p>

          <fieldset className="scelta-preavviso">
            <legend className="solo-lettori-schermo">Preavviso da dare ai colleghi</legend>
            {([
              [0, 'Subito', 'Nessun preavviso. Da usare quando si è certi che non stia lavorando nessuno.'],
              [300, 'Fra 5 minuti', 'Il tempo di finire quello che si ha aperto.'],
              [900, 'Fra 15 minuti', 'Per le ore in cui l\'ufficio è pieno.'],
            ] as const).map(([valore, titolo, spiegazione]) => (
              <label key={valore} className="scelta-preavviso-voce">
                <input
                  type="radio"
                  name="preavviso"
                  checked={preavviso === valore}
                  onChange={() => setPreavviso(valore)}
                  disabled={inCorso}
                />
                <span>
                  <strong>{titolo}</strong>
                  <span className="aiuto">{spiegazione}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <p>
            Se l'aggiornamento fallisce, il server rimette da sé la versione precedente e
            avvisa su Telegram.
          </p>
          <div className="dialogo-azioni">
            <button
              type="button"
              className="bottone bottone-principale"
              onClick={() => void installa()}
              disabled={inCorso}
            >
              {inCorso ? 'Invio…' : 'Installa ora'}
            </button>
            <button
              type="button"
              className="bottone-piatto"
              onClick={() => setChiedeConferma(false)}
              disabled={inCorso}
            >
              Annulla
            </button>
          </div>
        </Dialogo>
      )}
    </>
  );
}
